import {
  type CheckState,
  Chip,
  cn,
  EmptyNote,
  PatientHeader,
  ProgressChip,
  Skeleton,
  StateCheckbox,
  TimeLabel,
} from "@scribenote/brian-design-system";
import { DisplayFrame } from "@shell/DisplayFrame";
import { groupBy } from "@utils/group-by";
import { useNow } from "@utils/use-now";
import { type ReactNode, useMemo } from "react";

import {
  doneText,
  earlierSpan,
  earlierText,
  earlierTrail,
  foldEarlier,
  patientMeta,
  stepText,
  trailText,
} from "./treatmentBoardFormat";
import type {
  TreatmentLine,
  TreatmentPatient,
  TreatmentStep,
} from "./treatmentBoardView";

const SHEET =
  "rounded-paper border border-rule bg-card px-10 pt-6 pb-3 shadow-sheet";

const CHECK: Record<TreatmentStep["state"], CheckState> = {
  todo: "open",
  active: "open",
  done: "done",
};

const Row = ({
  state,
  text,
  trail,
  at,
  nested,
}: {
  state: CheckState;
  text: string;
  trail: string;
  at: string | null;
  nested?: boolean;
}) => (
  <div className={cn("flex items-start gap-[18px]", nested && "pl-11")}>
    {/* Never pressable: Brian ticks a row from what he hears, not from a tap. */}
    <StateCheckbox state={state} className="mt-px" />
    <div
      className={cn(
        "min-w-0 flex-1 leading-[1.3] text-ink",
        nested ? "text-[1.0625rem]" : "text-xl",
      )}
    >
      {text}
      <p className="mt-0.5 text-sm leading-[1.35] text-annotation">{trail}</p>
    </div>
    <span className="min-w-14 shrink-0 pt-1 text-right">
      {at && <TimeLabel value={at} />}
    </span>
  </div>
);

const Step = ({ step, nested }: { step: TreatmentStep; nested?: boolean }) => (
  <Row
    state={CHECK[step.state]}
    text={stepText(step)}
    trail={trailText(step)}
    at={step.progressAt}
    nested={nested}
  />
);

const Item = ({ children }: { children: ReactNode }) => (
  <li className="flex list-none flex-col gap-2 border-b border-rule py-2 last:border-b-0">
    {children}
  </li>
);

const Line = ({ line }: { line: TreatmentLine }) => (
  <Item>
    <Step step={line} />
    {line.components.map((component) => (
      <Step key={component.orderId} step={component} nested />
    ))}
  </Item>
);

const PatientSheet = ({
  patient,
  siblings,
  now,
}: {
  patient: TreatmentPatient;
  siblings: readonly TreatmentPatient[];
  now: number;
}) => {
  const { visit, lines } = patient;
  const done = lines.filter((line) => line.state === "done").length;
  const { shown, earlier } = foldEarlier(lines, now);

  return (
    <article
      data-testid="treatment-board-patient"
      data-dd-privacy="hidden"
      className={SHEET}
    >
      <div className="flex items-start justify-between gap-6 border-b-[1.5px] border-ink pb-4">
        <PatientHeader
          size="card"
          name={visit.name}
          meta={patientMeta(patient, siblings)}
          sprite={visit.sprite}
        />
        {lines.length > 0 &&
          (done === lines.length ? (
            <Chip tone="recorded" className="mt-2.5 shrink-0">
              {doneText(patient)}
            </Chip>
          ) : (
            <ProgressChip
              done={done}
              total={lines.length}
              className="shrink-0 pt-2.5"
            />
          ))}
      </div>
      {lines.length === 0 ? (
        <EmptyNote className="justify-start px-0 py-4 text-[1.1875rem]">
          Nothing for treatment yet.
        </EmptyNote>
      ) : (
        <ul>
          {shown.map((line) => (
            <Line key={line.orderId} line={line} />
          ))}
          {earlier.length > 0 && (
            <Item>
              <Row
                state="done"
                text={earlierText(earlier)}
                trail={earlierTrail(earlier)}
                at={earlierSpan(earlier).last}
              />
            </Item>
          )}
        </ul>
      )}
    </article>
  );
};

/**
 * Household siblings sit side by side, each household where its first patient
 * fell; a sheet stays one patient's because its checklist is.
 */
const seatByHousehold = (
  patients: readonly TreatmentPatient[],
): { patient: TreatmentPatient; siblings: TreatmentPatient[] }[] =>
  groupBy(patients, (patient) => patient.visit.householdId).flatMap(
    (household) =>
      household.map((patient) => ({
        patient,
        siblings: household.filter((other) => other !== patient),
      })),
  );

/** Every patient in treatment with their checklist; read-only, from the display's own feed. */
export const TreatmentBoardDisplay = ({
  name,
  patients,
}: {
  name: string;
  patients: TreatmentPatient[] | null;
}) => {
  const now = useNow();
  const sheets = useMemo(() => seatByHousehold(patients ?? []), [patients]);
  return (
    <DisplayFrame tapes={[name]}>
      {!patients ? (
        <div aria-busy="true" className="flex flex-col">
          <Skeleton shape="sheet" className="h-[30rem]" />
        </div>
      ) : patients.length === 0 ? (
        <p
          data-testid="treatment-board-clear"
          className="m-auto font-display text-[2.5rem] font-medium text-annotation"
        >
          No patients in treatment
        </p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pb-1">
          {sheets.map(({ patient, siblings }) => (
            <PatientSheet
              key={patient.visit.id}
              patient={patient}
              siblings={siblings}
              now={now}
            />
          ))}
        </div>
      )}
    </DisplayFrame>
  );
};
